import { createSelector } from 'reselect';
import moment from 'moment';
import { PagesMap } from '../../Models';
import { getPagesHistoryArray } from '../../containers/PagesHistory/selectors';

export interface StreakInfoProps {
  readonly longest: number;
  readonly current: number;
  readonly repairedDates: moment.Moment[];
}

export enum StreakType {
  none,
  normal,
  record,
}

export const streakType = (info: StreakInfoProps): StreakType => {
  if (info.current === 0) {
    return StreakType.none;
  } else if (info.current >= info.longest) {
    return StreakType.record;
  } else {
    return StreakType.normal;
  }
};

export const streakIcon = (type: StreakType): string => {
  return type === StreakType.record ? 'ic-streak-star' : type === StreakType.normal ? 'ic-streak-zap' : '';
};

const selectUserPagesHistoryState = (state: any): PagesMap => {
  return state.pagesHistory.items;
};

const shouldUseOldStreakLogic = (firstPageDate: string | undefined): boolean => {
  if (!firstPageDate) {
    return false;
  }

  const newRuleDate = moment('2019-09-20', 'YYYY-MM-DD');
  return moment(firstPageDate) < newRuleDate;
};

export const getStreakInfo = createSelector(
  getPagesHistoryArray,
  selectUserPagesHistoryState,
  (pages, pagesMap) => {
    let longestStreak = 0;
    let streak = 0;
    let latestStreak: number | null = null;

    const consecutiveDaysForRepairReward = 5;
    const maximumRepairDays = 14;
    let availableRepairDays = 0;
    let consecutiveWritingDays = 0;

    let gapDates: moment.Moment[] = [];
    const repairedDates: moment.Moment[] = [];

    const firstPage = pages[0];
    const shouldUseOldStreakRule = firstPage ? shouldUseOldStreakLogic(firstPage.startedWritingAt) : false;

    const firstPageDate = firstPage ? moment(firstPage.createdAt) : null;

    if (firstPageDate) {
      const endDate = moment(); // Today's date
      const iterDate = firstPageDate.clone(); // Starting from the first page's date

      while (iterDate.isSameOrBefore(endDate)) {
        const pageId = iterDate.format('YYYY-MM-DD');
        const isToday = iterDate.isSame(moment(), 'day');

        const page = pagesMap[pageId];
        let shouldCountAsStreak = false;

        if (page) {
          const startedWritingAt = moment(page.startedWritingAt);

          shouldCountAsStreak =
            shouldUseOldStreakRule ||
            iterDate.isSame(startedWritingAt, 'day') ||
            iterDate
              .clone()
              .add(1, 'day')
              .isSame(startedWritingAt, 'day');
        }

        if (shouldCountAsStreak) {
          if (gapDates.length <= availableRepairDays) {
            streak += gapDates.length;
            availableRepairDays -= gapDates.length;
            repairedDates.push(...gapDates);
          }

          gapDates = [];
          streak++;
          consecutiveWritingDays++;

          if (consecutiveWritingDays > 0 && consecutiveWritingDays % consecutiveDaysForRepairReward === 0) {
            availableRepairDays = Math.min(availableRepairDays + 1, maximumRepairDays);
            consecutiveWritingDays = 0;
          }
        } else {
          consecutiveWritingDays = 0;
          gapDates.push(iterDate.clone());

          if (gapDates.length > availableRepairDays && !isToday) {
            // Can't repair those missed days, reset tmp streak
            longestStreak = Math.max(streak, longestStreak);
            latestStreak = streak;
            streak = 0;
          }
        }

        iterDate.add(1, 'day'); // Move to the next date
      }
    }

    let canBeRepaired = false;
    if (!pagesMap[moment().format('YYYY-MM-DD')] && gapDates.length > 1 && gapDates.length - 1 <= availableRepairDays) {
      canBeRepaired = true;
      streak = latestStreak!;
    }

    return {
      longest: longestStreak,
      current: streak,
      repairedDates,
      accumulatedRepairDays: availableRepairDays,
      canBeRepaired,
      isStreakRepaired: (day: Date) => {
        return (
          repairedDates.find(date => {
            return date.toDate().toDateString() === day.toDateString();
          }) !== undefined
        );
      },
    };
  },
);



// WEBPACK FOOTER //
// ./src/components/StreakInfo/StreakLogic.tsx